/**
 * Footprint de dados locais / memória — telemetria de pressão de memória.
 * Tudo aqui é leitura e NUNCA lança: cada fonte tem try/catch próprio.
 */
import database from '../database'
import { recordEvent } from './events'

// Tabelas "principais" para medir volume (chave amigável -> tabela WatermelonDB).
export const FOOTPRINT_TABLES = {
  fazendas: 'farms',
  retiros: 'corrals',
  lotes: 'batches',
  matrizes: 'd0s', // 1 d0 por matriz/manejo inicial
  iatfs: 'iatfs',
  dns: 'dns',
} as const

export type RecordCounts = Partial<
  Record<keyof typeof FOOTPRINT_TABLES, number>
>

const MB = 1024 * 1024
export const toMb = (bytes: unknown): number | null =>
  typeof bytes === 'number' && isFinite(bytes) ? Math.round(bytes / MB) : null

export async function countRecords(db: any): Promise<RecordCounts> {
  const out: RecordCounts = {}
  await Promise.all(
    Object.entries(FOOTPRINT_TABLES).map(async ([key, table]) => {
      try {
        out[key as keyof RecordCounts] = await db.collections
          .get(table)
          .query()
          .fetchCount()
      } catch {
        /* tabela omitida */
      }
    }),
  )
  return out
}

/** localStorage.downloadFarms é um array de { id, lastPulledAt }. null = ilegível. */
export function readDownloadedFarmsCount(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage !== 'undefined'
    ? localStorage
    : null,
): number | null {
  try {
    const raw = storage?.getItem('downloadFarms')
    if (!raw) return 0
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr.length : null
  } catch {
    return null
  }
}

/** performance.memory só existe em Chrome/Edge; fora disso => null. */
export function readHeap(
  perf: any = typeof performance !== 'undefined' ? performance : undefined,
) {
  try {
    const m = perf?.memory
    if (!m) return null
    const heapUsedMb = toMb(m.usedJSHeapSize)
    const heapTotalMb = toMb(m.totalJSHeapSize)
    const heapLimitMb = toMb(m.jsHeapSizeLimit)
    if (heapUsedMb == null && heapTotalMb == null && heapLimitMb == null)
      return null
    return { heapUsedMb, heapTotalMb, heapLimitMb }
  } catch {
    return null
  }
}

export async function readStorage(
  nav: any = typeof navigator !== 'undefined' ? navigator : undefined,
) {
  try {
    const est = await nav?.storage?.estimate?.()
    if (!est) return null
    return {
      storageUsageMb: toMb(est.usage),
      storageQuotaMb: toMb(est.quota),
      idbUsageMb: toMb(est.usageDetails?.indexedDB), // Chrome; ausente nos demais
    }
  } catch {
    return null
  }
}

export type FootprintPayload = {
  farmsDownloaded: number | null
  records: RecordCounts
  heapUsedMb: number | null
  heapTotalMb: number | null
  heapLimitMb: number | null
  storageUsageMb: number | null
  storageQuotaMb: number | null
  idbUsageMb: number | null
}

export function buildFootprint(parts: {
  farmsDownloaded?: number | null
  records?: RecordCounts
  heap?: ReturnType<typeof readHeap>
  storage?: Awaited<ReturnType<typeof readStorage>>
}): FootprintPayload {
  return {
    farmsDownloaded: parts.farmsDownloaded ?? null,
    records: parts.records ?? {},
    heapUsedMb: parts.heap?.heapUsedMb ?? null,
    heapTotalMb: parts.heap?.heapTotalMb ?? null,
    heapLimitMb: parts.heap?.heapLimitMb ?? null,
    storageUsageMb: parts.storage?.storageUsageMb ?? null,
    storageQuotaMb: parts.storage?.storageQuotaMb ?? null,
    idbUsageMb: parts.storage?.idbUsageMb ?? null,
  }
}

export async function collectFootprint(
  db: any = database,
): Promise<FootprintPayload> {
  const [records, storage] = await Promise.all([
    countRecords(db),
    readStorage(),
  ])
  return buildFootprint({
    farmsDownloaded: readDownloadedFarmsCount(),
    records,
    heap: readHeap(),
    storage,
  })
}

export type FarmDownloadInfo = {
  outcome: 'completed' | 'failed'
  farmsCount: number
  fetchDurationMs?: number | null
  postFetchMs?: number | null // corpo + parse + aplicação no banco (tempo após os headers)
  elapsedMs?: number | null // só no 'failed'
  responseSizeKb?: number | null
  forceResync?: boolean
  fullRedownload?: boolean // = isRemoveLastPulledAtFarm (Sincronizar baixa tudo de novo)
  isRetry?: boolean
  batchesCount?: number
  hadLastPulledAt?: boolean
}

/**
 * Registra o footprint no fim de um download de fazendas. Fire-and-forget: o caller
 * usa `void recordFarmDownloadFootprint(...)`. Nunca lança e nunca atrasa o sync.
 * level 'info' mesmo na falha (evita push imediato; a falha já tem pull_farms_failed).
 */
export async function recordFarmDownloadFootprint(
  info: FarmDownloadInfo,
): Promise<void> {
  try {
    const footprint = await collectFootprint()
    await recordEvent({
      level: 'info',
      category: 'sync',
      action: 'farm_download_footprint',
      payload: { ...info, ...footprint },
    })
  } catch {
    /* telemetria nunca derruba o fluxo */
  }
}
